/**
 * viewportCommands — AutoCAD viewport command implementations.
 *
 * Commands:
 *   VPLAYER F <layer> <vpId>  — freeze layer in viewport
 *   VPLAYER T <layer> <vpId>  — thaw layer in viewport
 *   VPCLIP set <vpId> <bounds> — set rectangular clip boundary
 *   VPCLIP remove <vpId>      — remove clip
 *   MVSETUP scale <vpId> <num> <denom> — set viewport scale
 *   LOCK <vpId> <true|false>  — toggle VP lock
 *
 * All operate through EditorCommand dispatch (geometry/viewport-* types).
 * The mutation logic (applied inside the geometry reducer) lives here and
 * is imported by geometryReducerLayout.ts.
 *
 * AutoCAD references:
 *  - VPLAYER command: AutoCAD DXF Reference §VIEWPORT entity, group codes
 *    331–333 (frozen layer handles); AutoCAD help "VPLAYER command".
 *  - VPCLIP command: AutoCAD help "VPCLIP command".
 *  - MVSETUP command / CANNOSCALE: per-viewport scale stored in group 44
 *    (cannoscale factor) and group 144 (scale handle).
 *  - Display Locked: DXF group 68, bit 16 of viewportStatus.
 *
 * ezdxf reference: ezdxf.entities.Viewport
 */

import type {
  DrawingDocument,
  DrawingEntity,
  ViewportEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  writeViewportOverrides,
  readViewportOverrides,
  detachInvalidatedViewportClipObjects,
  nativeBoundaryClipPolygon,
  recomputeNativeClipPath,
  retainUnresolvedFrozenLayerHandles,
} from "@dxf-ai-editor/drawing-model";
import {
  invalidateViewportClip,
  NO_CLIP_HANDLE,
  VIEWPORT_NONRECT_CLIP_BIT,
} from "./viewportClipInvalidate";
import {
  setViewportLockEntity,
  setViewportScaleEntity,
  setViewportViewEntity,
} from "./viewportMutateEntity";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ViewportMutationResult = {
  /** Updated document. Same reference as input if no viewport matched. */
  document: DrawingDocument;
  /** True when the target viewport was found and mutated. */
  mutated: boolean;
  /**
   * CANONICAL entity id of the matched viewport (`vp.id`), set on a genuine
   * mutation. `findViewportById` matches the command's `viewportId` token
   * CASE-INSENSITIVELY (`VPCLIP 2a …` → stored `2A`), so the raw token can differ
   * from the stored id. Downstream audit/telemetry must record the affected
   * entity by its REAL stored id, not the raw token, or the admin detail row
   * loses the affected-viewport evidence (@codex #496 P2). Undefined on a no-op
   * (no viewport matched) — the audit emitter never fires in that case.
   */
  canonicalViewportId?: string;
};

// ---------------------------------------------------------------------------
// Internal helper: find a viewport entity by id across all spaces
// ---------------------------------------------------------------------------

/**
 * Normalize a DXF handle for CASE-INSENSITIVE comparison. DXF handles are hex
 * strings and producers (and users typing a VPCLIP target/boundary handle) are
 * inconsistent about case — `VPCLIP 2a …` must still match a viewport / boundary
 * object whose stored id is `2A`, or the command silently no-ops (@codex #496 P2,
 * mirrors dxf-io `normalizeHandle` + the OBJECTS/native-340 import resolvers,
 * which already key by normalized handle). Kept LOCAL (trim + lowercase, the
 * repo-wide convention) so command-engine adds no dxf-io dependency.
 */
function normalizeVpHandle(handle: string): string {
  return handle.trim().toLowerCase();
}

function findViewportById(
  doc: DrawingDocument,
  viewportId: string,
): ViewportEntity | null {
  const target = normalizeVpHandle(viewportId);
  for (const space of doc.spaces) {
    for (const entity of space.entities) {
      if (entity.type === "Viewport" && normalizeVpHandle(entity.id) === target) {
        return entity as ViewportEntity;
      }
    }
  }
  return null;
}

/**
 * Return a new DrawingDocument with the given viewport entity replaced.
 * Preserves all other entities and spaces.
 */
function replaceViewportEntity(
  doc: DrawingDocument,
  updated: ViewportEntity,
): DrawingDocument {
  return {
    ...doc,
    spaces: doc.spaces.map((space) => ({
      ...space,
      entities: space.entities.map((e) =>
        e.id === updated.id ? updated : e,
      ),
    })),
  };
}

// ---------------------------------------------------------------------------
// LOCK / UNLOCK — geometry/viewport-set-lock
// ---------------------------------------------------------------------------

/**
 * Set the displayLocked flag on a viewport entity.
 * Also writes the locked state into the VP's XDATA overrides block.
 * AutoCAD parity: MVIEW LOCK/MVIEW UNLOCK; the lock indicator (gold square)
 * appears on the viewport frame in ViewportFrameLayer.
 *
 * S15 Slice F: the entity transform (XDATA lock + group-90 0x4000 sync) is the
 * shared `setViewportLockEntity` the Properties-panel `viewportMutator` also calls,
 * so a panel Display-lock edit produces the identical entity state.
 */
export function applyViewportSetLock(
  doc: DrawingDocument,
  viewportId: string,
  locked: boolean,
): ViewportMutationResult {
  const vp = findViewportById(doc, viewportId);
  if (!vp) return { document: doc, mutated: false };
  // setViewportLockEntity returns `vp` UNCHANGED only for the paper-space control
  // viewport (viewportId=1); a real lock/unlock always yields a fresh clone.
  const updated = setViewportLockEntity(vp, locked);
  if (updated === vp) return { document: doc, mutated: false };

  return {
    document: replaceViewportEntity(doc, updated),
    mutated: true,
    canonicalViewportId: vp.id,
  };
}

// ---------------------------------------------------------------------------
// SHADE PLOT — geometry/viewport-set-shadeplot
// ---------------------------------------------------------------------------

/**
 * Set the shade-plot mode on a viewport entity (DXF group 170).
 *
 * AutoCAD parity: the per-viewport "Shade plot" plot-output setting on the
 * paper-space Properties palette / Plot dialog. This is a PLOT-TIME-ONLY
 * property — it controls how the viewport is rendered when the layout is
 * PLOTTED, NOT the on-screen display — so there is intentionally no scene /
 * render-path change here. The value is a FIRST-CLASS entity field (group 170),
 * not an XDATA override and not a status-bit flag, so we simply clone the
 * viewport with the new field (mirroring applyViewportSetLock's guards +
 * immutable-clone shape, minus the lock-specific XDATA + statusBitFlags sync).
 *
 * Base modes 0=As Displayed, 1=Wireframe, 2=Hidden, 3=Rendered are authored
 * here (the contract caps `shadePlotMode` at 0..3 — see editor.ts). Modes 4
 * (Visual Style) / 5 (Render Preset) reference a named visual-style /
 * render-preset handle (DXF 348 / 333) the data model does not yet carry — they
 * are IMPORT-ONLY, READ-ONLY, and a documented follow-up scope
 * (docs/test-gaps/viewport-shadeplot-visualstyle-modes.md). A viewport CURRENTLY
 * in mode 4/5 is therefore refused by construction (see the CURRENT-mode guard
 * below), so an imported 4/5 round-trips verbatim through parse → export and is
 * never re-authored / corrupted here.
 *
 * `shadePlotMode` is OPTIONAL:
 *   - A concrete 0..3 is a normal forward author (dropdown / MATCHPROP-apply).
 *   - `undefined` is the undo inverse RESTORING an absent DXF group-170 record on
 *     a viewport that had no group 170 (model field undefined) before the edit.
 *     Setting the key to `undefined` CLEARS the field so export omits group 170
 *     again (the writer emits it only when `shadePlotMode !== undefined`,
 *     writer.ts ~1104) — preserve-source, no spurious `170=0`.
 *
 * Two guards bracket the apply: a CURRENT-mode read-only guard (the target must
 * already be in-range 0..3 — undefined counts as 0/in-range), and a REQUESTED-
 * value guard rejecting a bare 4/5 from raw/programmatic callers. The latter is
 * load-bearing because `computeViewportEditMutation` is invoked WITHOUT re-parsing
 * through `editorCommandSchema`, so the contract's 0..3 cap cannot protect this
 * call site — the mutation must self-guard the REQUESTED value.
 *
 * ezdxf reference: ezdxf.entities.Viewport.dxf.shade_plot_mode.
 */
export function applyViewportSetShadePlot(
  doc: DrawingDocument,
  viewportId: string,
  shadePlotMode: number | undefined,
): ViewportMutationResult {
  const vp = findViewportById(doc, viewportId);
  if (!vp) return { document: doc, mutated: false };
  // Paper-space control viewport (DXF viewportId=1) must not be user-modifiable
  // (mirrors applyViewportSetLock) — the control viewport IS paper space and has
  // no plot-shade-mode of its own.
  if (vp.viewportId === 1) return { document: doc, mutated: false };

  // By-construction READ-ONLY guard on the CURRENT mode: a viewport whose CURRENT
  // shadePlotMode is outside the authorable base range 0..3 (i.e. an imported
  // Visual Style mode 4 / Render Preset mode 5) is deferred to the 348/333
  // visual-style-handle subsystem. Refusing the edit here prevents (a) a
  // contradictory base-mode-170 record that still carries the now-stale 348/333
  // handle in extraGroups, and (b) an out-of-range undo inverse the 0..3-capped
  // schema (editor.ts) would reject. The proper handle-add/drop on a 4/5
  // transition belongs to the deferred subsystem; until it ships, such viewports
  // never leave 4/5 here. `undefined` counts as 0 (in range, editable).
  const currentMode = vp.shadePlotMode ?? 0;
  if (currentMode < 0 || currentMode > 3) {
    return { document: doc, mutated: false };
  }

  // REQUESTED-value guard: reject a bare 4/5 (or any out-of-range / non-integer
  // number) coming from a raw/programmatic dispatch. `computeViewportEditMutation`
  // calls this WITHOUT re-parsing through `editorCommandSchema`, so the contract's
  // integer-0..3 cap does not protect this path — guard the REQUESTED value here.
  // `!Number.isInteger` rejects fractional values (1.5) AND NaN (whose `< 0` / `> 3`
  // comparisons are both false, so the range check alone would let it through).
  // `undefined` passes (it is the inverse's "restore absence", not an author of 4/5).
  if (
    shadePlotMode !== undefined &&
    (!Number.isInteger(shadePlotMode) || shadePlotMode < 0 || shadePlotMode > 3)
  ) {
    return { document: doc, mutated: false };
  }

  // Clone the viewport entity (immutable document pattern); shadePlotMode is a
  // direct DXF group-170 field, so set it directly and preserve all other
  // fields (scale / lock / frozenLayers / view state / clip). When
  // `shadePlotMode` is `undefined` the spread KEEPS the key with value undefined,
  // which the writer treats as field-absent (it emits group 170 only when
  // `shadePlotMode !== undefined`) — so an undo restoring absence omits group 170
  // again rather than writing a spurious `170=0`.
  const updated: ViewportEntity = { ...vp, shadePlotMode };

  return {
    document: replaceViewportEntity(doc, updated),
    mutated: true,
    canonicalViewportId: vp.id,
  };
}

// ---------------------------------------------------------------------------
// VPLAYER — geometry/viewport-vplayer
// ---------------------------------------------------------------------------

/**
 * Freeze or thaw a named layer in a single viewport.
 * AutoCAD parity: VPLAYER command with F (freeze) / T (thaw) options.
 * Per-viewport frozen layers are stored in both ViewportEntity.frozenLayers
 * (in-memory) and the EDIT_DWG_VP_OVERRIDES XDATA block (DXF round-trip).
 */
export function applyViewportVplayer(
  doc: DrawingDocument,
  viewportId: string,
  action: "freeze" | "thaw",
  layerName: string,
  options?: {
    // UNDO-restore of the pre-mutation NATIVE group-331 handle carrier
    // (`frozenLayerHandles`), snapshotted VERBATIM on the computed inverse and
    // present ONLY on the undo path — a forward (user/AI/panel) dispatch OMITS it:
    //   • string[] → restore that exact 331 list (source ORDER + CASING preserved),
    //   • null     → the viewport had NO native carrier → restore its ABSENCE,
    //   • omitted  → NORMAL forward: the positive thaw-drop below runs unchanged.
    // Mirrors the set-view inverse's verbatim `restore*` snapshots (preserve-
    // source-never-infer, @codex #486). Without it, undoing a thaw would re-derive
    // the layer handle from the table and APPEND it at the end, losing an
    // ordered/lower-cased source slot on imported viewports.
    restoreFrozenLayerHandles?: string[] | null;
  },
): ViewportMutationResult {
  const vp = findViewportById(doc, viewportId);
  if (!vp) return { document: doc, mutated: false };
  // Paper-space control viewport (DXF viewportId=1) must not be user-modifiable.
  if (vp.viewportId === 1) return { document: doc, mutated: false };

  const overrides = readViewportOverrides(vp);
  const layerSet = new Set(overrides.frozenLayers);

  if (action === "freeze") {
    if (layerSet.has(layerName)) {
      // Already frozen — no-op (idempotent)
      return { document: doc, mutated: false };
    }
    layerSet.add(layerName);
  } else {
    if (!layerSet.has(layerName)) {
      // Not frozen — no-op (idempotent)
      return { document: doc, mutated: false };
    }
    layerSet.delete(layerName);
  }

  overrides.frozenLayers = Array.from(layerSet);
  const updated: ViewportEntity = { ...vp };
  writeViewportOverrides(overrides, updated);
  // `sourceFrozenLayerHandles` (viewportFrozenLayers.ts) is the FULL verbatim
  // native-331 snapshot (order + casing) export prefers over the double-count-
  // safe `frozenLayerHandles` (danglers-only, post-reconciliation — @codex #486
  // P2). It is DELIBERATELY left untouched by a FREEZE below (a new name has no
  // existing 331 slot; nothing in the current native carrier becomes stale) and
  // is FILTERED — not wholesale cleared — by a THAW, mirroring exactly what
  // happens to `frozenLayerHandles` (@codex #514 P2 "Preserve the filtered
  // source handles on VPLAYER edits": unconditionally clearing it here lost
  // order/casing fidelity for every OTHER still-frozen layer untouched by this
  // specific edit).

  const restoreHandles = options?.restoreFrozenLayerHandles;
  if (restoreHandles !== undefined) {
    // UNDO path — restore the pre-mutation native 331 carrier VERBATIM so a
    // thaw+undo round-trips the original handle ORDER + CASING byte-faithfully
    // (the forward thaw-drop below is intentionally LOSSY — see its note). This
    // supersedes the positive thaw-drop: the snapshot already encodes the exact
    // prior state, so we never re-derive/re-order. `null` restores absence.
    //
    // The FULL verbatim list restores onto `sourceFrozenLayerHandles`, NOT
    // directly onto `frozenLayerHandles` (@codex #514 P2 "Keep undo source
    // handles out of the projected carrier"): `frozenLayerHandles` must stay
    // the double-count-safe danglers-only carrier the agent-bridge projector
    // reads as a PARALLEL, non-deduped array alongside `frozenLayers` names
    // (@codex #486 P2) — restoring RESOLVED handles into it would re-introduce
    // the exact double-count the reconciliation exists to prevent. Re-derive
    // the reduced carrier from the restored source via the SAME filter
    // `resolveViewportFrozenLayerNames` uses on import.
    const fullRestore =
      restoreHandles && restoreHandles.length > 0
        ? [...restoreHandles]
        : undefined;
    updated.sourceFrozenLayerHandles = fullRestore;
    updated.frozenLayerHandles = fullRestore
      ? retainUnresolvedFrozenLayerHandles(
          fullRestore,
          doc.styleTables?.layers ?? [],
        )
      : undefined;
    return {
    document: replaceViewportEntity(doc, updated),
    mutated: true,
    canonicalViewportId: vp.id,
  };
  }

  // EXPORT-VP-FROZEN — a THAW is a POSITIVE edit on the NATIVE group-331 carrier,
  // not merely a name removal. The frozen-layer NAMES and the native
  // `frozenLayerHandles` are two views of the SAME freeze set, so drop the thawed
  // layer's LAYER-record handle here too. Without this, thawing the LAST frozen
  // layer leaves a stale resolvable handle that the export (which unions resolvable
  // handle names back into the XDATA carrier) would re-surface — silently re-
  // freezing a thawed layer — and would be indistinguishable from a legitimate
  // handle-only native freeze (which MUST be preserved). Freeze needs no symmetric
  // add: the exporter derives a fresh name's 331 handle from the layer table. The
  // undo of THIS lossy drop is the `restoreFrozenLayerHandles` verbatim snapshot.
  if (action === "thaw") {
    const thawedHandle = doc.styleTables?.layers
      ?.find((l) => l.name.toLowerCase() === layerName.toLowerCase())
      ?.handle?.trim()
      .toLowerCase();
    if (thawedHandle) {
      if (updated.frozenLayerHandles?.length) {
        const remaining = updated.frozenLayerHandles.filter(
          (h) => h.trim().toLowerCase() !== thawedHandle,
        );
        updated.frozenLayerHandles =
          remaining.length > 0 ? remaining : undefined;
      }
      // Apply the SAME filter to the verbatim source carrier (@codex #514 P2
      // "Preserve the filtered source handles on VPLAYER edits") — only the
      // thawed layer's slot is dropped, every OTHER still-frozen layer keeps
      // its original source order/casing.
      if (updated.sourceFrozenLayerHandles?.length) {
        const remainingSource = updated.sourceFrozenLayerHandles.filter(
          (h) => h.trim().toLowerCase() !== thawedHandle,
        );
        updated.sourceFrozenLayerHandles =
          remainingSource.length > 0 ? remainingSource : undefined;
      }
    }
  }

  return {
    document: replaceViewportEntity(doc, updated),
    mutated: true,
    canonicalViewportId: vp.id,
  };
}

// ---------------------------------------------------------------------------
// VPCLIP — geometry/viewport-clip-set / geometry/viewport-clip-remove
// ---------------------------------------------------------------------------

/**
 * Set a rectangular clip boundary on a viewport.
 * AutoCAD parity: VPCLIP with a rectangular boundary.
 * The rectangular clip is expressed by reshaping the viewport itself (center +
 * width + height to match the clip bounds), mirroring how AutoCAD VPCLIP-
 * rectangle reshapes the user-picked viewport rectangle. A separate persisted
 * non-rectangular clip boundary (OBJECTS SPATIAL_FILTER + its DXF-340 handle on
 * ViewportEntity.clippingBoundaryHandle) is the deferred slice-3 authoring scope
 * (S-VIEWPORT-CLIP-RESOLVE) — this command therefore INVALIDATES any imported
 * 340 handle (below) rather than synthesising a new boundary. Full polygon
 * VPCLIP authoring is a future scope item.
 */
export function applyViewportClipSet(
  doc: DrawingDocument,
  viewportId: string,
  bounds: { x1: number; y1: number; x2: number; y2: number },
): ViewportMutationResult {
  const vp = findViewportById(doc, viewportId);
  if (!vp) return { document: doc, mutated: false };
  // Paper-space control viewport (DXF viewportId=1) must not be user-modifiable.
  // VPCLIP reshapes the user-picked viewport rectangle; the control viewport IS
  // paper space itself, so AutoCAD VPCLIP does not apply to it.
  if (vp.viewportId === 1) return { document: doc, mutated: false };

  const newCX = (bounds.x1 + bounds.x2) / 2;
  const newCY = (bounds.y1 + bounds.y2) / 2;
  const newW = Math.abs(bounds.x2 - bounds.x1);
  const newH = Math.max(1, Math.abs(bounds.y2 - bounds.y1));

  // Preserve the effective scale (viewHeight / vpHeight) when the clip
  // rectangle changes the viewport height.  If we left viewHeight unchanged
  // while vpHeight changed, the implied scale would shift.
  const oldH = vp.height ?? 0;
  const oldViewHeight = vp.viewHeight ?? 0;
  const newViewHeight =
    oldH > 0 && oldViewHeight > 0
      ? oldViewHeight * (newH / oldH)
      : oldViewHeight;

  const reshaped: ViewportEntity = {
    ...vp,
    center: { x: newCX, y: newCY },
    width: Math.max(1, newW),
    height: newH,
    viewHeight: newViewHeight,
  };
  // S-VIEWPORT-CLIP-HANDLE (PR #318) — VPCLIP rectangular REPLACES the clip
  // boundary. If the source viewport carried a native clip boundary, every
  // persisted artefact (typed 340 handle, group-90 0x10000 non-rect bit, and a
  // legacy extraGroups-340 from pre-typed-field documents) now refers to the
  // OLD, now-superseded boundary. Invalidate ALL of them via the shared helper
  // so export does not re-emit a 340 / non-rect flag pointing at a
  // dangling/orphaned boundary. (Re-authoring a NEW SPATIAL_FILTER + 340 for the
  // new rectangle is the deferred slice-3 VPCLIP-authoring scope —
  // S-VIEWPORT-CLIP-RESOLVE; this slice only round-trips the imported handle, so
  // the correct behaviour for a clip MUTATION today is to invalidate, not
  // synthesise.)
  const cleared = invalidateViewportClip(reshaped);

  // Codex #326 P2 (round-2) — VPCLIP-set RESHAPES the view rectangle (it just
  // rewrote center/width/height + rescaled viewHeight above). The membership
  // index (`childEntityIds`) is derived from that view rectangle
  // (buildViewportMembershipIndex over viewCenter/viewHeight/width/height), so
  // after the reshape the OLD child set no longer describes what the viewport
  // shows. `invalidateViewportClip` intentionally PRESERVES `childEntityIds`
  // (it is independent of the CLIP BOUNDARY, which is all that helper owns), so
  // the view-extent change must be handled HERE, at the command that actually
  // changes the view rectangle. Drop the stale membership so the streaming /
  // import membership finalize (or the next render-time recompute) rebuilds it
  // for the NEW extents rather than feeding the rectangular reshape a child set
  // computed for the pre-clip view.
  const updated =
    cleared.childEntityIds !== undefined
      ? (() => {
          const next: ViewportEntity = { ...cleared };
          delete next.childEntityIds;
          return next;
        })()
      : cleared;

  // Codex #326 r3 P2 #3 — a SPATIAL_FILTER-sourced clip is also persisted in the
  // OBJECTS round-trip (the viewport ext-dict → ACAD_FILTER → SPATIAL_FILTER chain
  // carried verbatim on `rawObjectGroups`). Dropping the typed handle + render
  // polygon alone leaves that chain intact, so export re-emits it and a reopen
  // RE-RESOLVES the supposedly-replaced clip. Detach the chain at the document
  // level (preserving any unrelated ext-dict entries) so the new rectangular clip
  // is not overridden by the stale boundary on reload.
  const withEntity = replaceViewportEntity(doc, updated);
  return {
    document: detachInvalidatedViewportClipObjects(withEntity, [updated]),
    mutated: true,
    canonicalViewportId: vp.id,
  };
}

/** Find the id of the space that CONTAINS the given viewport (null if none). */
function findViewportSpaceId(
  doc: DrawingDocument,
  viewportId: string,
): string | null {
  const target = normalizeVpHandle(viewportId);
  for (const space of doc.spaces) {
    if (
      space.entities.some(
        (e) => e.type === "Viewport" && normalizeVpHandle(e.id) === target,
      )
    )
      return space.id;
  }
  return null;
}

/**
 * Find an entity by id WITHIN a single space only. The VPCLIP "Select clipping
 * object" boundary lookup must NOT cross layout / model-space frames: a closed
 * polyline in model space or another paper-space layout lives in a DIFFERENT
 * coordinate frame, so its vertices are meaningless as THIS viewport's paper-
 * space clip (@codex #496 P2). Restricting the lookup to the viewport's own
 * space guarantees the resolved boundary is in the correct frame. The id match
 * is CASE-INSENSITIVE (DXF handles are hex, case-insensitive): `VPCLIP <vp> O 3f`
 * must resolve a boundary object whose stored id is `3F` (@codex #496 P2).
 */
function findEntityInSpace(
  doc: DrawingDocument,
  spaceId: string,
  entityId: string,
): DrawingEntity | null {
  const space = doc.spaces.find((s) => s.id === spaceId);
  if (!space) return null;
  const target = normalizeVpHandle(entityId);
  return space.entities.find((e) => normalizeVpHandle(e.id) === target) ?? null;
}

/**
 * Signed area (shoelace) of a polygon. `|area| ≈ 0` ⟺ the vertices enclose NO
 * region — three duplicate/collinear points, or a zero-area closed polyline —
 * which is not a valid clip boundary (@codex #496 P2).
 */
function polygonSignedArea(
  pts: ReadonlyArray<{ x: number; y: number }>,
): number {
  let area = 0;
  for (let i = 0; i < pts.length; i += 1) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    area += a.x * b.y - b.x * a.y;
  }
  return area / 2;
}

/**
 * Minimum enclosed `|area|` (paper units²) a VPCLIP boundary must have. A polygon
 * below this is degenerate (all-duplicate / all-collinear vertices, or a zero-
 * area closed polyline) and would author a clip that renders / round-trips
 * incorrectly, so it is rejected BEFORE mutating (@codex #496 P2). Deliberately
 * tiny — it rejects ONLY genuinely-degenerate polygons, never a legitimately
 * small clip.
 */
const MIN_CLIP_POLYGON_AREA = 1e-9;

/**
 * Per-coordinate tolerance for "does the boundary OBJECT still resolve to the
 * FROZEN polygon?" (@codex #496 P2 redo). Both polygons come from the SAME
 * deterministic `nativeBoundaryClipPolygon` tessellation, so an unedited object
 * matches exactly; the epsilon only absorbs benign floating-point noise, never
 * a genuine geometry edit.
 */
const CLIP_BOUNDARY_MATCH_EPS = 1e-9;

/**
 * True when two paper-space polygons are vertex-for-vertex equal within
 * {@link CLIP_BOUNDARY_MATCH_EPS}. Used to decide whether a boundary object's
 * CURRENT polygon still matches the frozen polygon a redo restored (@codex #496
 * P2). A different vertex count, or any vertex moved beyond the epsilon, means
 * the object diverged.
 */
function clipPolygonsMatch(
  a: ReadonlyArray<{ x: number; y: number }>,
  b: ReadonlyArray<{ x: number; y: number }>,
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (
      Math.abs(a[i]!.x - b[i]!.x) > CLIP_BOUNDARY_MATCH_EPS ||
      Math.abs(a[i]!.y - b[i]!.y) > CLIP_BOUNDARY_MATCH_EPS
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Resolve the PAPER-space clip polygon for a VPCLIP polygonal authoring request
 * — the SINGLE source of truth shared by the producer
 * (`applyViewportClipSetPolygon`) and the store's redo-freeze (editorStore,
 * @codex #496 P2 redo). Returns the polygon, or `null` when the request cannot
 * author a valid clip (so EVERY caller no-ops identically):
 *   • an explicit `boundary` (≥3 vertices) WINS — VPCLIP Polygonal / scripted;
 *   • else a `boundaryEntityId` is resolved to its closed-boundary polygon via
 *     the SHARED `nativeBoundaryClipPolygon`, BUT ONLY when the object lives in
 *     the SAME space as the viewport (a boundary in model space / another layout
 *     is a different coordinate frame — @codex #496 P2);
 *   • the resolved polygon must have ≥3 vertices AND a non-degenerate enclosed
 *     area (@codex #496 P2).
 */
export function resolveVpclipBoundaryPolygon(
  doc: DrawingDocument,
  viewportId: string,
  opts: {
    boundary?: ReadonlyArray<{ x: number; y: number }>;
    boundaryEntityId?: string;
  },
): Array<{ x: number; y: number }> | null {
  let polygon: Array<{ x: number; y: number }> | null = null;
  if (opts.boundary && opts.boundary.length >= 3) {
    polygon = opts.boundary.map((p) => ({ x: p.x, y: p.y }));
  } else if (opts.boundaryEntityId) {
    const spaceId = findViewportSpaceId(doc, viewportId);
    if (!spaceId) return null;
    const boundaryEntity = findEntityInSpace(
      doc,
      spaceId,
      opts.boundaryEntityId,
    );
    polygon = nativeBoundaryClipPolygon(boundaryEntity ?? undefined);
  }
  if (!polygon || polygon.length < 3) return null;
  if (Math.abs(polygonSignedArea(polygon)) < MIN_CLIP_POLYGON_AREA) return null;
  return polygon;
}

/**
 * Author a NON-rectangular (polygonal) clip boundary on a viewport — the
 * PRODUCER half of the VPCLIP "Polygonal" / "Select clipping object" command
 * (EPIC-VIEWPORT-CLIP slice-3 authoring; the render stencil that consumes the
 * result is already armed — dxfGeometry projects `clipPath` onto the renderer's
 * `__viewport_clip_path` preview property).
 *
 * The clip boundary is a TYPED object-graph — a paper-space polygon
 * (`clipBoundaryPaperSpace`) plus its derived model-space projection
 * (`clipPath`), NOT a boolean flag (ПРИОРИТЕТ #1). The boundary source is one of:
 *   • `boundary` — an explicit ≥3-vertex PAPER-space polygon (VPCLIP Polygonal /
 *     scripted points). A `boundary` present always WINS.
 *   • `boundaryEntityId` — a closed boundary ENTITY (VPCLIP Select clipping
 *     object) resolved to its polygon via the SHARED `nativeBoundaryClipPolygon`
 *     (the SAME closed-LWPOLYLINE + bulge tessellation the import-time native-340
 *     resolver uses — one truth, no simplification). Curved boundary types
 *     (CIRCLE/ELLIPSE/closed SPLINE/REGION) are the deferred
 *     S-VIEWPORT-CLIP-340-CURVED slice and resolve to `null` → mutated:false.
 *
 * Authoring semantics (mirrors `applyViewportClipSet` for the invalidation side):
 *   1. Any PRIOR imported clip (SPATIAL_FILTER 340 handle, native-340 paper
 *      boundary, group-90 0x10000 bit, resolved `clipPath`) is INVALIDATED via
 *      the shared `invalidateViewportClip` so the new polygon fully REPLACES it
 *      (a re-clip must not leave a stale/dangling boundary behind), and the
 *      persisted OBJECTS SPATIAL_FILTER chain is detached at the document level.
 *   2. The new paper-space polygon is stored on `clipBoundaryPaperSpace`, the
 *      group-90 non-rectangular-clip bit (0x10000) is set (paired with the
 *      boundary per the Autodesk DXF reference), and `clipPath` is derived by the
 *      SHARED `recomputeNativeClipPath` paper→model projection (so a later
 *      pan/zoom re-projects it from the fixed paper boundary, AutoCAD parity).
 *
 * The VIEW RECTANGLE (viewCenter/viewHeight/width/height) is UNCHANGED — a
 * polygonal clip narrows the visible region WITHIN the window without moving the
 * window — so `childEntityIds` (membership, derived from the view rectangle)
 * stays valid and is intentionally NOT cleared (unlike `applyViewportClipSet`,
 * which reshapes the rectangle).
 *
 * DXF export of the authored boundary (synthesising a new SPATIAL_FILTER + 340 on
 * write) is the separate EXPORT-VP-FROZEN writer scope (#486); this producer owns
 * the in-session typed authoring + render + undo/redo. Undo restores the FULL
 * pre-mutation viewport snapshot (geometryMutations `computeInverse` →
 * geometry/restore-viewport-clip).
 *
 * ezdxf reference: ezdxf `Viewport.clipping_boundary_handle` (340) +
 * `acdb_viewport` group-90 non-rect-clip bit; xclip.py boundary chain.
 */
export function applyViewportClipSetPolygon(
  doc: DrawingDocument,
  viewportId: string,
  opts: {
    boundary?: ReadonlyArray<{ x: number; y: number }>;
    boundaryEntityId?: string;
  },
): ViewportMutationResult {
  const vp = findViewportById(doc, viewportId);
  if (!vp) return { document: doc, mutated: false };
  // Paper-space control viewport (DXF viewportId=1) IS paper space itself — VPCLIP
  // does not apply to it (mirrors the sibling clip/lock guards).
  if (vp.viewportId === 1) return { document: doc, mutated: false };

  // Resolve the boundary polygon in PAPER space via the SHARED resolver (one
  // truth with the store's redo-freeze): an explicit `boundary` wins; a bare
  // `boundaryEntityId` is resolved against the viewport's OWN space only (incl.
  // bulge tessellation). A curved / open / missing / wrong-space object, or a
  // degenerate (<3-vertex / zero-area) polygon, resolves to null → no-op so a
  // bad request never authors a corrupt clip.
  const paperBoundary = resolveVpclipBoundaryPolygon(doc, viewportId, opts);
  if (!paperBoundary) {
    return { document: doc, mutated: false };
  }

  // @codex #496 P1 — for OBJECT-mode VPCLIP (VPCLIP <vp> O <obj>) the boundary
  // object ALREADY exists in the drawing, so record ITS handle as the viewport's
  // 340 clip-boundary handle. The DXF export path emits a viewport clip ONLY from
  // a real 340 (`maskOrphanNonRectClipBit` clears the non-rect bit when no handle
  // is present), so without this an object-based clip is silently LOST on
  // save/reopen even though the boundary entity is right there to reference — and
  // the import native-340 resolver (`applyViewportClipAndMembership`) reads exactly
  // this 340 → paper-space boundary to rebuild the clip. Resolve the CANONICAL
  // boundary handle (the entity's own group-5 id, via the case-insensitive
  // in-space lookup) so the exported 340 matches its target's handle EXACTLY. An
  // explicit-POINTS polygon (`boundary`, no object) has no persisted boundary
  // entity yet — synthesising a SPATIAL_FILTER + 340 on write is the deferred #486
  // writer slice — so it stays handle-less and the orphan bit is masked on export.
  let objectBoundaryHandle: string | undefined;
  if (opts.boundaryEntityId) {
    const spaceId = findViewportSpaceId(doc, viewportId);
    const boundaryEntity = spaceId
      ? findEntityInSpace(doc, spaceId, opts.boundaryEntityId)
      : null;
    // @codex #496 P2 (redo divergence) — the store's history REDO-freeze records
    // BOTH the frozen `boundary` polygon AND the `boundaryEntityId` (editorStore),
    // so redo replays the EXACT authored polygon while still re-recording the
    // object's 340 export handle. If the boundary OBJECT was EDITED between undo
    // and redo, its CURRENT polygon no longer matches that frozen `boundary`.
    // Recording the live 340 then would make export/reopen re-resolve the CHANGED
    // object and render a clip that CONTRADICTS the frozen polygon redo restored
    // in-session (@codex #496 P2). So only keep the handle when the object STILL
    // resolves to the frozen polygon; otherwise drop it and the redone clip stays
    // handle-less — exactly like an explicit-points polygon, whose SPATIAL_FILTER
    // synthesis is the deferred #486 writer slice — so the persisted clip can
    // never diverge from what redo restored. The object-ONLY forward (no explicit
    // `boundary`) needs no check: `paperBoundary` IS the object's own polygon, so
    // the normal authoring path always keeps its handle.
    const objectStillMatchesFrozen =
      !opts.boundary ||
      (() => {
        const objectPolygon = nativeBoundaryClipPolygon(
          boundaryEntity ?? undefined,
        );
        return (
          objectPolygon !== null && clipPolygonsMatch(objectPolygon, paperBoundary)
        );
      })();
    if (objectStillMatchesFrozen) {
      objectBoundaryHandle = boundaryEntity?.id;
    }
  }

  // (1) Supersede any prior imported clip artefacts on the ENTITY.
  const cleared = invalidateViewportClip(vp);

  // (2) Author the new paper-space boundary + set the non-rect clip bit, then
  //     derive the model-space `clipPath` via the shared paper→model projection.
  const withBoundary: ViewportEntity = {
    ...cleared,
    clipBoundaryPaperSpace: paperBoundary,
    statusBitFlags:
      (cleared.statusBitFlags ?? 0) | VIEWPORT_NONRECT_CLIP_BIT,
  };
  // (2a) Record the object boundary's 340 handle (@codex #496 P1) — OR drop any
  //      "0" no-clip sentinel `invalidateViewportClip` preserved (typed
  //      `clippingBoundaryHandle` + legacy `extraGroups`-340). A viewport
  //      authoring a REAL clip must NOT still advertise `340 "0"`: that
  //      contradictory state makes sentinel-honouring export/reopen paths treat
  //      the newly clipped viewport as UNclipped (@codex #496 P2).
  if (objectBoundaryHandle !== undefined) {
    withBoundary.clippingBoundaryHandle = objectBoundaryHandle;
  } else {
    if (withBoundary.clippingBoundaryHandle === NO_CLIP_HANDLE) {
      delete withBoundary.clippingBoundaryHandle;
    }
    if (withBoundary.extraGroups?.some((g) => g.code === 340)) {
      const kept = withBoundary.extraGroups.filter((g) => g.code !== 340);
      if (kept.length > 0) withBoundary.extraGroups = kept;
      else delete withBoundary.extraGroups;
    }
  }
  const modelPath = recomputeNativeClipPath(withBoundary);
  const updated: ViewportEntity =
    modelPath && modelPath.length >= 3
      ? { ...withBoundary, clipPath: modelPath.map((p) => ({ x: p.x, y: p.y })) }
      : withBoundary;

  // (3) Detach any persisted imported SPATIAL_FILTER OBJECTS chain so a reopen
  //     does not re-resolve the superseded clip (mirrors applyViewportClipSet).
  const withEntity = replaceViewportEntity(doc, updated);
  return {
    document: detachInvalidatedViewportClipObjects(withEntity, [updated]),
    mutated: true,
    canonicalViewportId: vp.id,
  };
}

/**
 * Remove the clip boundary from a viewport (VPCLIP Delete).
 * The viewport's rectangular shape IS the (default) clip boundary, so there is
 * no separate rectangle field to reset here. The ONE persisted clip artefact in
 * this phase is the native clip-boundary handle (DXF 340, S-VIEWPORT-CLIP-HANDLE
 * PR #318) carried verbatim from an imported non-rectangular-clipped viewport.
 * VPCLIP Delete must invalidate that handle so export stops re-emitting a 340
 * pointing at the removed boundary. The command otherwise stays for API
 * completeness + audit trail.
 */
export function applyViewportClipRemove(
  doc: DrawingDocument,
  viewportId: string,
): ViewportMutationResult {
  const vp = findViewportById(doc, viewportId);
  if (!vp) return { document: doc, mutated: false };
  // Paper-space control viewport (DXF viewportId=1) must not be user-modifiable.
  // VPCLIP Delete does not apply to the control viewport (it IS paper space);
  // without this guard clip-remove reports mutated=true and emits a false
  // "VPCLIP removed" success audit for the control viewport.
  if (vp.viewportId === 1) return { document: doc, mutated: false };
  // S-VIEWPORT-CLIP-HANDLE (PR #318) — invalidate EVERY persisted clip artefact
  // (typed 340 handle, group-90 0x10000 non-rect bit, and a legacy
  // extraGroups-340 from pre-typed-field documents) via the shared helper so
  // export no longer re-emits a stale group 340 / non-rect flag after the clip
  // is removed. (Detaching/erasing the referenced OBJECTS SPATIAL_FILTER
  // boundary entity is the deferred slice-3 scope — S-VIEWPORT-CLIP-RESOLVE;
  // this slice only owns the typed handle round-trip, so removal == invalidating
  // those artefacts.)
  const updated = invalidateViewportClip(vp);
  // Codex #326 r3 P2 #3 — VPCLIP Delete must ALSO detach the persisted
  // SPATIAL_FILTER chain from the OBJECTS round-trip (ext-dict → ACAD_FILTER →
  // SPATIAL_FILTER on `rawObjectGroups`). Dropping only the typed handle + the
  // resolved `clipPath` leaves that chain intact, so export re-emits it and a
  // reopen re-resolves the "deleted" clip. The detach is keyed by the viewport's
  // `extensionDictionaryHandle` (present on `vp`/`updated` regardless of whether
  // the entity-side artefacts changed), so run it even when the entity-side
  // invalidate was a no-op (a clip whose ONLY carrier was the OBJECTS chain).
  const withEntity = updated !== vp ? replaceViewportEntity(doc, updated) : doc;
  const detached = detachInvalidatedViewportClipObjects(withEntity, [updated]);
  // mutated=true regardless (AutoCAD VPCLIP Delete reports success), but the
  // document is unchanged only when neither the entity nor the OBJECTS changed.
  return { document: detached, mutated: true, canonicalViewportId: vp.id };
}

// ---------------------------------------------------------------------------
// MVSETUP scale — geometry/viewport-set-scale
// ---------------------------------------------------------------------------

/**
 * Set the drawing scale for a viewport (MVSETUP scale).
 * AutoCAD parity: the VP's viewHeight is recalculated so the drawing
 * renders at the requested scale relative to the paper sheet.
 *
 * Scale formula: viewHeight = (vpHeight on paper) × (denom / num)
 * where vpHeight is the viewport's height in paper-space units (mm or
 * inches depending on drawing units). The cannoscale fields are updated
 * so annotative objects render at the right size through this viewport.
 *
 * The scale is also written to the XDATA overrides for round-trip.
 */
export function applyViewportSetScale(
  doc: DrawingDocument,
  viewportId: string,
  scaleNum: number,
  scaleDenom: number,
): ViewportMutationResult {
  const vp = findViewportById(doc, viewportId);
  if (!vp) return { document: doc, mutated: false };
  // S15 Slice F — the entity transform (viewHeight re-derive + cannoscale +
  // vpOverrideScale + XDATA scale + native-clip re-project) is the shared
  // `setViewportScaleEntity` the Properties-panel `viewportScaleMutator` also calls.
  // It returns `vp` UNCHANGED only for the paper-space control viewport
  // (viewportId=1) — a scale (MVSETUP) does not apply to it (it IS paper space).
  const updated = setViewportScaleEntity(vp, scaleNum, scaleDenom);
  if (updated === vp) return { document: doc, mutated: false };

  return {
    document: replaceViewportEntity(doc, updated),
    mutated: true,
    canonicalViewportId: vp.id,
  };
}

// ---------------------------------------------------------------------------
// SET VIEW (pan / zoom) — geometry/viewport-set-view
// ---------------------------------------------------------------------------

/**
 * Persist a viewport's pan/zoom view state (MC4 data-model primitive).
 *
 * AutoCAD parity: a viewport's view state is defined by two first-class DXF
 * fields on the VIEWPORT entity:
 *   - viewCenter (DXF group 12/22) — the view centre in the DISPLAY coordinate
 *     system, whose origin is the WCS view TARGET (17/27/37). The model-space
 *     point shown at the centre of the viewport is therefore `viewTarget +
 *     R(viewTwistAngle)·viewCenter` — the DCS axes are turned by the twist —
 *     (`drawing-model`'s `viewportWcsViewCenter`), NOT this field
 *     alone; the field stays DCS because that is what the group codes are and
 *     what the DXF round-trip and the Properties palette preserve, so a
 *     pointer-derived centre must be un-offset with
 *     `viewportStoredViewCenterFromWcs` before it reaches this command.
 *     Changing it = PAN.
 *   - viewHeight (DXF group 45) — the model-space height of the view, i.e. the
 *     zoom magnification. Changing it = ZOOM.
 *
 * Unlike scale/lock (which live in XDATA overrides), viewCenter and viewHeight
 * are written DIRECTLY to the entity fields because they ARE DXF group codes.
 *
 * Derived scale: a viewport's displayed scale is NOT independently authoritative
 * — it is derived as scale_factor = paperHeight / viewHeight, where paperHeight
 * is the VP rectangle height (group 41, paper units). So when viewHeight changes
 * (free-zoom), we recompute vpOverrideScale = { num:1, denom: round(viewHeight /
 * paperHeight) } so the status-bar selector (ViewportStatusControls /
 * ViewportInspector) reflects reality and falls to "Custom" when the
 * magnification is off any named preset. The XDATA overrides scale is kept in
 * sync via writeViewportOverrides so the scale survives DXF round-trip.
 *
 * Both viewCenter and viewHeight are optional: a pan-only update passes
 * viewCenter, a zoom-only update passes viewHeight, and a combined pan+zoom
 * passes both. No-op (same values, or neither provided) returns mutated=false.
 */
export function applyViewportSetView(
  doc: DrawingDocument,
  viewportId: string,
  view: {
    viewCenter?: { x: number; y: number };
    viewHeight?: number;
    // UNDO-restore of the pre-pan/zoom displayed scale (preserve-source). Present
    // ONLY on the computed inverse — a forward (user/AI/panel) dispatch omits it.
    // When present the lossy re-derive is SKIPPED and the snapshotted values are
    // restored VERBATIM. The displayed scale has TWO independent carriers that can
    // DIVERGE, restored separately (see `setViewportViewEntity`):
    //   • restoreVpOverrideScale = the typed `vpOverrideScale` entity field
    //     (`{num,denom}` restores that rational, `null` restores its ABSENCE),
    //   • restoreXdataScale      = the VP_OVERRIDES XDATA scale codes
    //     (`{num,denom}` re-writes them, `null` strips them).
    // Mirrors the shade-plot inverse's verbatim `prior`.
    restoreVpOverrideScale?: { num: number; denom: number } | null;
    restoreXdataScale?: { num: number; denom: number } | null;
  },
): ViewportMutationResult {
  const vp = findViewportById(doc, viewportId);
  if (!vp) return { document: doc, mutated: false };
  // S15 Slice F — the pan/zoom entity transform (viewCenter/viewHeight write,
  // no-op detection, scale re-derive OR the two-carrier undo restore, native-clip
  // re-project) is the shared `setViewportViewEntity` the Properties-panel
  // `viewportMutator` (View height / View center X/Y) also calls. It returns `vp`
  // UNCHANGED for the control viewport (viewportId=1) OR any no-op (nothing
  // supplied / values match), so `mutated` derives from `updated !== vp`.
  const updated = setViewportViewEntity(vp, view);
  if (updated === vp) return { document: doc, mutated: false };

  return {
    document: replaceViewportEntity(doc, updated),
    mutated: true,
    canonicalViewportId: vp.id,
  };
}
